<script setup lang="ts">
/**
 * UI-kit component
 * Category: Dropdown
 * Name: 'AppDropdownItem'
 * FIGMA: https://www.figma.com/design/oRsJUOAucnhxx5aeqy4TRO/APP-admin?node-id=2989-37302
 */
import { computed } from 'vue'

import type { AppDropdownItemProps } from '../types/interfaces'

import { faSquare } from '@fortawesome/pro-regular-svg-icons'
import { faSquareCheck } from '@fortawesome/pro-solid-svg-icons'

import { AppText } from '@shared/ui/typography'
import { AppIcon } from '@shared/ui/icons'

import { useAppDropdownItem } from '../composables/use-app-dropdown-item.hook'

defineOptions({
  name: 'AppDropdownItem',
})

const props = withDefaults(defineProps<AppDropdownItemProps>(), {
  selected: false,
  disabled: false,
  checkbox: false,
  prependIcon: undefined,
  appendIcon: undefined,
  description: undefined,
  tag: 'button',
  color: 'default',
})

const emit = defineEmits<{
  click: []
}>()

const {
  itemClasses,
  textColor,
  iconColor,
  handleClick,
} = useAppDropdownItem({
  props,
  onClick: () => emit('click'),
})

const checkboxIcon = computed(() => props.selected ? faSquareCheck : faSquare)
</script>

<template>
  <component
    :is="tag"
    :type="tag === 'button' ? 'button' : undefined"
    :class="itemClasses"
    :disabled="tag === 'button' ? disabled : undefined"
    :aria-pressed="tag === 'button' ? (selected || undefined) : undefined"
    @click="handleClick"
  >
    <span
      v-if="$slots.avatar"
      class="app-dropdown-item__avatar"
    >
      <slot name="avatar" />
    </span>

    <AppIcon
      v-if="checkbox"
      :icon="checkboxIcon"
      :color="selected ? 'emerald-600' : 'slate-400'"
      class="app-dropdown-item__checkbox"
    />

    <span
      v-if="$slots['prepend-icon'] || prependIcon"
      class="app-dropdown-item__prepend"
    >
      <slot name="prepend-icon">
        <AppIcon
          v-if="prependIcon"
          :icon="prependIcon"
          :color="iconColor"
        />
      </slot>
    </span>

    <span class="app-dropdown-item__content">
      <AppText
        name="body1"
        :color="textColor"
      >
        {{ text }}
      </AppText>

      <slot name="description">
        <AppText
          v-if="description"
          name="body2"
          :color="disabled ? 'slate-300' : 'slate-500'"
        >
          {{ description }}
        </AppText>
      </slot>
    </span>

    <span
      v-if="$slots['status-tag']"
      class="app-dropdown-item__status-tag"
    >
      <slot name="status-tag" />
    </span>

    <span
      v-if="$slots['append-icon'] || appendIcon"
      class="app-dropdown-item__append"
    >
      <slot name="append-icon">
        <AppIcon
          v-if="appendIcon"
          :icon="appendIcon"
        />
      </slot>
    </span>
  </component>
</template>

<style scoped lang="scss" src="../styles/_app-dropdown-item.scss" />
